import { ErrorSeverity } from '../context/ErrorContext';
import { getErrorSeverity, getErrorGuidance } from './errorHandler';

/**
 * エラーメッセージとHTTPステータスコードから適切な通知オプションを生成
 */
export interface ErrorNotificationOptions {
  message: string;
  severity: ErrorSeverity;
  details?: string;
  guidance?: string;
  statusCode?: number;
  dismissible: boolean;
  autoHideDuration: number;
  action?: {
    label: string;
    handler: () => void;
  };
}

/**
 * HTTPステータスコードとエラーメッセージから通知オプションを生成
 */
export const createErrorNotification = (
  errorMessage: string,
  statusCode?: number,
  customOptions?: Partial<ErrorNotificationOptions>
): ErrorNotificationOptions => {
  const severity = getErrorSeverity(errorMessage, statusCode);
  const guidance = getErrorGuidance(errorMessage, statusCode);

  // デフォルトの自動非表示時間（エラーの重要度に応じて変更）
  const autoHideDuration = severity === 'error' ? 8000 : severity === 'warning' ? 6000 : 4000;

  // 再試行可能なエラーの場合のアクション
  let action: ErrorNotificationOptions['action'];
  if (statusCode === 401) {
    action = {
      label: '再ログイン',
      handler: () => {
        // 現在のURLを保存して、ログイン後に戻れるようにする
        const currentPath = window.location.pathname + window.location.search;
        // ルートパスやログインページ自体の場合は保存しない
        if (currentPath !== '/' && !currentPath.startsWith('/login')) {
          sessionStorage.setItem('redirectAfterLogin', currentPath);
        }

        // 状態保存の確認ダイアログを表示（重要なデータがある場合）
        const hasUnsavedData = document.querySelector('[data-unsaved]');
        if (hasUnsavedData) {
          const confirmRedirect = window.confirm(
            '保存されていないデータがあります。ログイン画面に移動してもよろしいですか？'
          );
          if (!confirmRedirect) {
            return;
          }
        }

        // ログインページへリダイレクト
        window.location.href = '/login';
      }
    };
  } else if (statusCode && statusCode >= 500) {
    action = {
      label: '再試行',
      handler: () => {
        // 名前空間付きのキーで衝突を防ぐ
        const NAMESPACE = 'niconico_error_handler_';
        const retryCountKey = `${NAMESPACE}500_retry_count`;
        const lastRetryKey = `${NAMESPACE}500_last_retry`;
        const retryAttemptsKey = `${NAMESPACE}500_retry_attempts`;

        const currentTime = Date.now();
        const lastRetryTime = parseInt(sessionStorage.getItem(lastRetryKey) || '0', 10);
        const retryCount = parseInt(sessionStorage.getItem(retryCountKey) || '0', 10);

        // リトライ履歴を取得（エクスポネンシャルバックオフ用）
        let retryAttempts: number[] = [];
        try {
          const stored = sessionStorage.getItem(retryAttemptsKey);
          if (stored) {
            retryAttempts = JSON.parse(stored);
          }
        } catch (e) {
          retryAttempts = [];
        }

        // 5分以内の再試行回数をカウント
        if (currentTime - lastRetryTime > 5 * 60 * 1000) {
          // 5分経過していたらカウントをリセット
          sessionStorage.setItem(retryCountKey, '0');
          sessionStorage.setItem(lastRetryKey, currentTime.toString());
          sessionStorage.setItem(retryAttemptsKey, '[]');
          retryAttempts = [];
        } else if (retryCount >= 3) {
          // 5分以内に3回以上再試行していたら警告
          alert(
            'エラーが繰り返し発生しています。しばらく時間をおいてから再度お試しください。' +
              '\n\n問題が続く場合は、システム管理者にお問い合わせください。'
          );
          return;
        }

        // エクスポネンシャルバックオフの計算（1秒、2秒、4秒...最大30秒）
        const baseDelay = 1000; // 1秒
        const maxDelay = 30000; // 30秒
        const delay = Math.min(baseDelay * Math.pow(2, retryCount), maxDelay);
        const delaySeconds = Math.ceil(delay / 1000);

        // 再試行の確認（待機時間を表示）
        const confirmMessage =
          retryCount === 0
            ? 'エラーが発生しました。ページを再読み込みして再試行しますか？'
            : `エラーが発生しました。${delaySeconds}秒後に再試行しますか？\n\n` +
              `(これは${retryCount + 1}回目の再試行です)`;

        const confirmRetry = window.confirm(confirmMessage);

        if (confirmRetry) {
          // 再試行回数と履歴を更新
          sessionStorage.setItem(retryCountKey, (retryCount + 1).toString());
          sessionStorage.setItem(lastRetryKey, currentTime.toString());
          retryAttempts.push(currentTime);
          sessionStorage.setItem(retryAttemptsKey, JSON.stringify(retryAttempts));

          // 待機時間がある場合は、カウントダウンを表示
          if (retryCount > 0 && delay > 0) {
            // 一時的な通知要素を作成
            const countdownDiv = document.createElement('div');
            countdownDiv.style.cssText = `
              position: fixed;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              background: rgba(0, 0, 0, 0.8);
              color: white;
              padding: 20px 40px;
              border-radius: 8px;
              font-size: 18px;
              z-index: 9999;
              text-align: center;
            `;

            let remainingSeconds = delaySeconds;
            const updateCountdown = () => {
              countdownDiv.textContent = `再試行まで ${remainingSeconds} 秒...`;
              if (remainingSeconds <= 0) {
                document.body.removeChild(countdownDiv);
                window.location.reload();
              } else {
                remainingSeconds--;
                setTimeout(updateCountdown, 1000);
              }
            };

            document.body.appendChild(countdownDiv);
            updateCountdown();
          } else {
            // 初回はすぐにリロード
            window.location.reload();
          }
        }
      }
    };
  }

  return {
    message: errorMessage,
    severity,
    details: guidance || undefined,
    statusCode,
    dismissible: true,
    autoHideDuration,
    action,
    ...customOptions
  };
};

/**
 * ネットワークエラー用の通知オプション
 */
export const createNetworkErrorNotification = (): ErrorNotificationOptions => {
  return {
    message: 'ネットワーク接続エラー',
    severity: 'error',
    details: 'インターネット接続を確認してください。問題が続く場合は管理者にお問い合わせください。',
    dismissible: true,
    autoHideDuration: 10000,
    action: {
      label: '再接続',
      handler: () => {
        // 名前空間付きのキーで衝突を防ぐ
        const NAMESPACE = 'niconico_error_handler_';
        const retryCountKey = `${NAMESPACE}network_retry_count`;
        const lastRetryKey = `${NAMESPACE}network_last_retry`;
        const retryAttemptsKey = `${NAMESPACE}network_retry_attempts`;

        const currentTime = Date.now();
        const lastRetryTime = parseInt(sessionStorage.getItem(lastRetryKey) || '0', 10);
        const retryCount = parseInt(sessionStorage.getItem(retryCountKey) || '0', 10);

        // リトライ履歴を取得
        let retryAttempts: number[] = [];
        try {
          const stored = sessionStorage.getItem(retryAttemptsKey);
          if (stored) {
            retryAttempts = JSON.parse(stored);
          }
        } catch (e) {
          retryAttempts = [];
        }

        // 5分以内の再試行回数をカウント
        if (currentTime - lastRetryTime > 5 * 60 * 1000) {
          sessionStorage.setItem(retryCountKey, '0');
          sessionStorage.setItem(lastRetryKey, currentTime.toString());
          sessionStorage.setItem(retryAttemptsKey, '[]');
          retryAttempts = [];
        } else if (retryCount >= 3) {
          alert(
            'ネットワークエラーが繰り返し発生しています。' +
              '\n\nインターネット接続を確認し、しばらく時間をおいてから再度お試しください。'
          );
          return;
        }

        // エクスポネンシャルバックオフの計算（2秒、4秒、8秒...最大30秒）
        const baseDelay = 2000; // 2秒（ネットワークエラーは少し長めに）
        const maxDelay = 30000; // 30秒
        const delay = Math.min(baseDelay * Math.pow(2, retryCount), maxDelay);
        const delaySeconds = Math.ceil(delay / 1000);

        // 再接続の確認（待機時間を表示）
        const confirmMessage =
          retryCount === 0
            ? 'ネットワーク接続を再試行しますか？\n\n' +
              '※インターネット接続が安定していることを確認してください。'
            : `ネットワーク接続を${delaySeconds}秒後に再試行しますか？\n\n` +
              `(これは${retryCount + 1}回目の再試行です)\n` +
              '※インターネット接続が安定していることを確認してください。';

        const confirmRetry = window.confirm(confirmMessage);

        if (confirmRetry) {
          // 再試行回数と履歴を更新
          sessionStorage.setItem(retryCountKey, (retryCount + 1).toString());
          sessionStorage.setItem(lastRetryKey, currentTime.toString());
          retryAttempts.push(currentTime);
          sessionStorage.setItem(retryAttemptsKey, JSON.stringify(retryAttempts));

          // 待機時間がある場合は、カウントダウンを表示
          if (retryCount > 0 && delay > 0) {
            // 一時的な通知要素を作成
            const countdownDiv = document.createElement('div');
            countdownDiv.style.cssText = `
              position: fixed;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              background: rgba(0, 0, 0, 0.8);
              color: white;
              padding: 20px 40px;
              border-radius: 8px;
              font-size: 18px;
              z-index: 9999;
              text-align: center;
            `;

            let remainingSeconds = delaySeconds;
            const updateCountdown = () => {
              countdownDiv.textContent = `再接続まで ${remainingSeconds} 秒...`;
              if (remainingSeconds <= 0) {
                document.body.removeChild(countdownDiv);
                window.location.reload();
              } else {
                remainingSeconds--;
                setTimeout(updateCountdown, 1000);
              }
            };

            document.body.appendChild(countdownDiv);
            updateCountdown();
          } else {
            // 初回はすぐにリロード
            window.location.reload();
          }
        }
      }
    }
  };
};

/**
 * バリデーションエラー用の通知オプション
 */
export const createValidationErrorNotification = (
  fieldErrors: Array<{ field: string; message: string }>
): ErrorNotificationOptions => {
  const errorMessages = fieldErrors.map((err) => `${err.field}: ${err.message}`).join('\n');

  return {
    message: '入力内容に誤りがあります',
    severity: 'warning',
    details: errorMessages,
    dismissible: true,
    autoHideDuration: 8000
  };
};

/**
 * 成功通知用のオプション
 */
export const createSuccessNotification = (
  message: string,
  details?: string
): ErrorNotificationOptions => {
  return {
    message,
    severity: 'success',
    details,
    dismissible: true,
    autoHideDuration: 3000
  };
};

/**
 * 情報通知用のオプション
 */
export const createInfoNotification = (
  message: string,
  details?: string
): ErrorNotificationOptions => {
  return {
    message,
    severity: 'info',
    details,
    dismissible: true,
    autoHideDuration: 5000
  };
};

/**
 * ネットワークエラーかどうかを判定
 */
const isNetworkError = (error: any): boolean => {
  // TypeError with fetch-related messages
  if (error instanceof TypeError) {
    const networkErrorMessages = [
      'fetch',
      'Failed to fetch',
      'NetworkError',
      'Network request failed',
      'ERR_INTERNET_DISCONNECTED',
      'ERR_NETWORK_CHANGED'
    ];
    if (
      networkErrorMessages.some((msg) => error.message?.toLowerCase().includes(msg.toLowerCase()))
    ) {
      return true;
    }
  }

  // Check for common network error codes
  const networkErrorCodes = [
    'ECONNREFUSED',
    'ENOTFOUND',
    'ETIMEDOUT',
    'ECONNRESET',
    'EHOSTUNREACH',
    'ENETUNREACH',
    'ECONNABORTED',
    'EPIPE',
    'EADDRNOTAVAIL'
  ];

  if (error.code && networkErrorCodes.includes(error.code)) {
    return true;
  }

  // Check for axios-specific network errors
  if (error.isAxiosError) {
    // No response means network error
    if (!error.response && error.request) {
      return true;
    }
    // Check for network error message
    if (error.message?.toLowerCase().includes('network error')) {
      return true;
    }
  }

  // Check for XMLHttpRequest errors
  if (error.request && !error.response) {
    return true;
  }

  // Check for specific error names
  const networkErrorNames = ['NetworkError', 'TimeoutError', 'AbortError'];
  if (error.name && networkErrorNames.includes(error.name)) {
    return true;
  }

  // Check for status 0 which often indicates network failure
  if (error.status === 0 && error.statusText === '') {
    return true;
  }

  return false;
};

/**
 * エラーオブジェクトから通知オプションを生成
 */
const createNotificationFromError = (error: any): ErrorNotificationOptions => {
  // ネットワークエラー
  if (isNetworkError(error)) {
    return createNetworkErrorNotification();
  }

  // HTTPエラー（statusプロパティを持つ場合）
  if (error.status) {
    const message = error.message || 'エラーが発生しました';
    return createErrorNotification(message, error.status);
  }

  // バリデーションエラー（detailが配列の場合）
  if (error.detail && Array.isArray(error.detail)) {
    const fieldErrors = error.detail
      .filter((item: any) => item.msg && item.loc)
      .map((item: any) => ({
        field: Array.isArray(item.loc) ? item.loc.join('.') : item.loc,
        message: item.msg
      }));

    if (fieldErrors.length > 0) {
      return createValidationErrorNotification(fieldErrors);
    }
  }

  // その他のエラー
  const message = error.message || error.detail || error.error || 'エラーが発生しました';
  return createErrorNotification(message);
};

/**
 * エラーコンテキストと統合されたエラーハンドリング
 */
export class ErrorNotificationHelper {
  private static errorContextCallback: ((options: ErrorNotificationOptions) => void) | null = null;

  /**
   * ErrorContextのshowError関数を登録
   */
  static setErrorContext(callback: (options: ErrorNotificationOptions) => void): void {
    this.errorContextCallback = callback;
  }

  /**
   * エラー通知を表示
   */
  static notify(error: any): void {
    const options = createNotificationFromError(error);
    if (ErrorNotificationHelper.errorContextCallback) {
      ErrorNotificationHelper.errorContextCallback(options);
    } else {
      // ErrorContextが利用できない場合はコンソールに出力
      console.error('ErrorContext not available:', options);
    }
  }

  /**
   * カスタムメッセージで通知
   */
  static notifyCustom(
    message: string,
    severity: ErrorSeverity = 'error',
    options?: Partial<ErrorNotificationOptions>
  ): void {
    const notificationOptions: ErrorNotificationOptions = {
      message,
      severity,
      dismissible: true,
      autoHideDuration: 6000,
      ...options
    };

    if (ErrorNotificationHelper.errorContextCallback) {
      ErrorNotificationHelper.errorContextCallback(notificationOptions);
    } else {
      console.error('ErrorContext not available:', notificationOptions);
    }
  }

  /**
   * 成功通知
   */
  static success(message: string, details?: string): void {
    const options = createSuccessNotification(message, details);
    if (ErrorNotificationHelper.errorContextCallback) {
      ErrorNotificationHelper.errorContextCallback(options);
    } else {
      // ErrorContextが利用できない場合のフォールバック
      console.log('Success notification (ErrorContext not available):', {
        message,
        details,
        severity: 'success'
      });
      // 可能であればブラウザのネイティブ通知を使用
      if (
        typeof window !== 'undefined' &&
        window.Notification &&
        Notification.permission === 'granted'
      ) {
        new Notification(message, { body: details || '', icon: '/favicon.ico' });
      }
    }
  }

  /**
   * 情報通知
   */
  static info(message: string, details?: string): void {
    const options = createInfoNotification(message, details);
    if (ErrorNotificationHelper.errorContextCallback) {
      ErrorNotificationHelper.errorContextCallback(options);
    } else {
      // ErrorContextが利用できない場合のフォールバック
      console.info('Info notification (ErrorContext not available):', {
        message,
        details,
        severity: 'info'
      });
      // 可能であればブラウザのネイティブ通知を使用
      if (
        typeof window !== 'undefined' &&
        window.Notification &&
        Notification.permission === 'granted'
      ) {
        new Notification(message, { body: details || '', icon: '/favicon.ico' });
      }
    }
  }

  /**
   * 警告通知
   */
  static warning(message: string, details?: string): void {
    ErrorNotificationHelper.notifyCustom(message, 'warning', { details });
  }
}
